import { ColorTypeButton } from '@/shared';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Card, IconButton, Tooltip, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { esES } from '@mui/x-date-pickers/locales';
import 'dayjs/locale/es';
import {
  MaterialReactTable,
  MRT_RowData,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_ES } from 'material-react-table/locales/es';
import React from 'react';
export interface GeneralTableProps<T> {
  columns: any;
  data: T[];

  isLoading?: boolean;
  isRefetching?: boolean;

  ///* search
  enableGlobalFilter?: boolean;
  onGlobalFilterChange?: (value: any) => void;
  enableColumnFilters?: boolean;

  ///* paging
  pagination: any;
  onPaging: any;
  rowCount?: number;
  paginationDisplayMode?: 'custom' | 'default' | 'pages';

  ///* actions
  actionsColumnSize?: number;

  // crud
  canEdit?: boolean;
  canEditByRowData?: (original: T) => boolean;
  onEdit?: (original: T) => void;
  onCustomEdit?: (original: T) => React.ReactNode;

  canDelete?: boolean;
  onDelete?: (original: T) => void;

  canAdd?: boolean;
  onAdd?: (original: T) => void;
  //
  enableSorting?: boolean;

  //
  positionActionsColumn?: 'first' | 'last';

  //
  enableColumnActions?: boolean;
  enableEditing?: boolean;

  //
  enableManualFiltering?: boolean;
  onColumnFiltersChange?: (filters: any) => void;
  columnFilters?: any;
  columnFilterDisplayMode?: 'custom' | 'subheader' | 'popover';

  //select
  density?: 'compact' | 'comfortable' | 'spacious';
  enableTopToolbar?: boolean;

  editIcon?: any;
  toolTipTitleEditIcon?: string;
  editIconColor?: ColorTypeButton;

  showCustomActionBtnSpace?: boolean;
  customActionBtnSpace?: (original: T) => React.ReactNode;

  enableHoverEffect?: boolean;
  paintRow?: (original: T) => string;
  enableSelectAll?: boolean;
  enableRowSelection?: boolean;
  rowSelection?: any;
  setRowSelection?: any;
  getRowId?: (originalRow: T) => string;

  onSortingChange?: (data: any) => void
  sorting?: any
  manualSorting?: boolean
  pinLeftColumnIndexes?: number[],
  enablePagination?: boolean,
  ///* columnas visibles por defecto (las ocultas siguen en el menu de columnas)
  initialColumnVisibility?: Record<string, boolean>,
  muiTableContainerProps?: React.ComponentProps<typeof MaterialReactTable>['muiTableContainerProps']
}

function GeneralTable<T>({
  columns,
  data,

  isLoading = false,
  isRefetching = false,

  ///* search
  enableGlobalFilter = true,
  onGlobalFilterChange,
  enableColumnFilters = true,

  ///* paging
  pagination,
  onPaging,
  rowCount = 0,
  paginationDisplayMode = 'default',

  ///* actions
  actionsColumnSize = 90,
  canEdit = true,
  canEditByRowData = (v) => v ? true : false,
  onEdit,
  canDelete,
  onDelete,
  canAdd,
  onAdd,
  onCustomEdit = undefined,

  //
  enableSorting = false,

  //
  positionActionsColumn = 'first',

  //
  enableColumnActions = true,
  enableEditing = true,

  // filtering
  enableManualFiltering = false,
  onColumnFiltersChange,
  columnFilters,
  columnFilterDisplayMode = enableManualFiltering ? 'subheader' : 'popover',

  // select
  density = 'comfortable',
  editIcon,
  enableTopToolbar = true,

  toolTipTitleEditIcon = 'Editar',
  editIconColor,

  showCustomActionBtnSpace = false,
  customActionBtnSpace,

  enableHoverEffect = false,
  paintRow,
  enableSelectAll = false,
  enableRowSelection = false,
  rowSelection, setRowSelection,
  getRowId,
  onSortingChange,
  sorting,
  manualSorting = false,
  pinLeftColumnIndexes,
  enablePagination = true,
  initialColumnVisibility,
  muiTableContainerProps

}: Readonly<GeneralTableProps<T>>) {
  /* const pinnedLeft = React.useMemo(() => {
    if (!enableRowSelection) return undefined;

    const left: string[] = ['mrt-row-select'];

    pinLeftColumnIndexes?.forEach((i) => {
      const key = columns[i]?.accessorKey;
      if (key) left.push(key as string);
    });

    return {left};
  }, [pinLeftColumnIndexes, columns, enableRowSelection]); */
  const columnPinning = React.useMemo(() => {
    //const hasCheckedRows =
     // Object.keys(rowSelection ?? {}).length > 0;

    //if (!hasCheckedRows) return undefined;

    const left: string[] = ['mrt-row-actions','mrt-row-select' ];

    pinLeftColumnIndexes?.forEach((i) => {
      const key = columns[i]?.accessorKey;
      if (key) left.push(key as string);
    });

    return { left };
  }, [rowSelection, pinLeftColumnIndexes, columns]);

  /*  const pinnedLeft = React.useMemo(() => {
     //if (!pinLeftColumnIndexes?.length) return undefined
     if (!enableRowSelection) return undefined
     const result: string[] = [];
 
     // columnas internas de MRT
 
     if (enableRowSelection) {
       result.push('mrt-row-actions');
       result.push('mrt-row-select');
     }
 
 
     // tus columnas por índice
     if (pinLeftColumnIndexes?.length) {
       pinLeftColumnIndexes.forEach((i) => {
         const key = columns[i]?.accessorKey;
         if (key) result.push(key as string);
       });
     }
 
     return result.length ? result : undefined;
   }, [pinLeftColumnIndexes, columns, enableRowSelection]); */
  ///* defining table
  const table = useMaterialReactTable({
    columns,
    data: data as MRT_RowData[],
    ...(getRowId && { getRowId: (row) => getRowId(row as T) }),

    localization: MRT_Localization_ES,
    enableEditing: enableEditing,
    enableDensityToggle: true,
    enableFullScreenToggle: false,
    enableTopToolbar: enableTopToolbar,
    // layoutMode: 'grid',

    ///* filtering
    ...(enableManualFiltering && {
      manualFiltering: enableManualFiltering, // server-side filtering by columns
      onColumnFiltersChange: onColumnFiltersChange,
    }),

    ///* search
    enableGlobalFilter: enableGlobalFilter,
    positionGlobalFilter: 'left',
    // manualFiltering: true,
    onGlobalFilterChange: onGlobalFilterChange,
    enableColumnFilters: enableColumnFilters,
    columnFilterDisplayMode: columnFilterDisplayMode,

    ///* paging
    manualPagination: true,
    onPaginationChange: onPaging,
    rowCount: rowCount,
    muiPaginationProps: {
      //rowsPerPageOptions: [10],
      // showRowsPerPage: false,
      showRowsPerPage: true,
    },

    paginationDisplayMode: paginationDisplayMode,

    ///* ordenar 
    manualSorting: manualSorting,
    //enableSorting: enableSorting,
    ...(enableSorting && { onSortingChange }),

    ///* actions - Solo incluir si enableColumnActions es true
    ...(enableColumnActions ? {
      enableColumnActions: true,
      positionActionsColumn: positionActionsColumn,
      displayColumnDefOptions: { 'mrt-row-actions': { size: actionsColumnSize } },
      renderRowActions: ({ row }) => {
        return (
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            {onCustomEdit ? (
              onCustomEdit(row.original as T)
            ) : (canEdit && canEditByRowData(row.original as T)) && (
              <Tooltip title={toolTipTitleEditIcon} placement='top'>
                <IconButton
                  onClick={() => {
                    onEdit && onEdit(row.original as T);
                  }}
                  color={editIconColor}
                >
                  {editIcon || <EditIcon />}
                </IconButton>
              </Tooltip>
            )}

            {canAdd && (
              <Tooltip title="Seleccionar">
                {/* <Button
                  color='success'
                  size='medium'
                  variant='outlined'
                  onClick={() => {
                    onAdd && onAdd(row.original as T);
                  }}
                  style={{
                    borderRadius: '8px'
                  }}
                >
                  Agregar
                </Button> */}
                <IconButton
                  onClick={() => {
                    onAdd && onAdd(row.original as T);
                  }}
                >
                  {<AddCircleIcon color="success" />}
                </IconButton>
              </Tooltip>
            )}

            {canDelete && (
              <Tooltip title="Eliminar">
                <IconButton
                  onClick={() => {
                    onDelete && onDelete(row.original as T);
                  }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Tooltip>
            )}

            {showCustomActionBtnSpace && customActionBtnSpace
              ? customActionBtnSpace(row.original as T)
              : null}
          </Box>
        );
      }
    } : {
      enableColumnActions: false,
    }),

    renderTopToolbarCustomActions: () => (
      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
        Total de Registros: {rowCount}
      </Typography>
    ),

    muiTableBodyRowProps: ({ row }) => ({
      hover: enableHoverEffect,
      sx: {
        backgroundColor: paintRow ? paintRow(row.original as T) : undefined
      }
    }),

    //
    muiTablePaperProps: {
      elevation: 0,
    },

    ...(muiTableContainerProps ? { muiTableContainerProps } : {}),

    ///* state compact, comfortable, spacious
    initialState: {
      showGlobalFilter: false,
      density: density,
      ...(initialColumnVisibility && { columnVisibility: initialColumnVisibility }),
     /*  ...(pinnedLeft && {
        columnPinning: { left: pinnedLeft }
      }) */

    },
    muiTableBodyCellProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },

    state: {
      ...(enableSorting && { sorting }),
      expanded: true,
      pagination,
      isLoading: isLoading || isRefetching,
      // filters
      ...(enableManualFiltering && { columnFilters }),
      // search bar
      //showLoadingOverlay: false,
      showProgressBars: isRefetching,
      //rowSelection
      ...(enableRowSelection && { rowSelection }),
      ...(columnPinning && { columnPinning }),
    },

    //
    /* renderEmptyRowsFallback: () => (
      <Typography textAlign={'center'}
        sx={{ fontStyle: 'italic', padding: '10px' }}
      >No hay registros para mostrar.</Typography>
    ), */

    //select
    // enableRowSelection: false,
    /* enableSelectAll: enableSelectAll,
    enableRowSelection: enableRowSelection,
    onRowSelectionChange: setRowSelection, */
    ...(enableRowSelection && {
      enableRowSelection: () => true,
      enableSelectAll: enableSelectAll,
      //enableRowSelection: enableRowSelection,
      onRowSelectionChange: setRowSelection,
      positionToolbarAlertBanner: 'top'
    }),
    enablePagination: enablePagination
  });

  return (
    <Card>
      <Box sx={{ width: '100%' }}>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          localeText={
            esES.components.MuiLocalizationProvider.defaultProps.localeText
          }
          adapterLocale="es"
        >
          <MaterialReactTable table={table} />
        </LocalizationProvider>
      </Box>
    </Card>
  );
}

export default GeneralTable;
